// =============================================================================
// SIREPPAR - Vista de Detalle del Expediente de Candidatura (Card D)
// =============================================================================

import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, candidaturaExpedienteService, documentoService, partidoService, seccionesDocumentoService, candidatoService, expedienteService } from '../../services/api';
import AdjuntosInforme, { MAX_MB_TOTAL_OBSERVACION, superaTotalAdjuntos } from '../../components/common/AdjuntosInforme';
import { useAuth } from '../../contexts/AuthContext';
import PanelSeccionesDocumentacion from './expediente/PanelSeccionesDocumentacion';
import Modal from '../../components/common/Modal';
import ModalResolucionTep, { ResolucionTepConfig } from '../../components/common/ModalResolucionTep';
import ModalOpcionesAccion, { type OpcionAccion } from '../../components/common/ModalOpcionesAccion';
import RichTextEditor, { MarkdownDisplay } from '../../components/common/RichTextEditor';
import ModalInformeSimple, { InformeSimpleConfig } from '../../components/common/ModalInformeSimple';
import { tipoDocLabels, TIPOS_CANDIDATURA, TIPOS_RESPUESTA_AGRUPACION } from '../../utils/tiposDocumento';
import { conEncabezadoInforme } from '../../utils/informeEncabezado';

// Texto abreviado de cada inciso del Art. 72 CE (Ley XII-21), tal como lo
// pasó la Secretaría — no redactado desde el código.
const TEXTOS_INCISO: Record<string, string> = {
  a: 'Personas excluidas del padrón por disposiciones legales vigentes.',
  b: 'Personal superior y subalterno de las Fuerzas Armadas de la Nación en actividad, o retirado llamado a prestar servicios.',
  c: 'Personal de fuerzas de seguridad nacional o provincial en actividad, o retirado llamado a prestar servicios.',
  d: 'Magistrados y funcionarios permanentes del Poder Judicial (Nación, provincias, CABA, Tribunales de Faltas Municipales).',
  e: 'Cargos directivos o apoderados de empresas concesionarias de servicios y obras públicas, o de empresas que exploten juegos de azar.',
  f: 'Personas con autos de procesamiento por genocidio, crímenes de lesa humanidad, crímenes de guerra u otras violaciones graves de los Derechos Humanos.',
  g: 'Personas condenadas por los crímenes descriptos en el inciso f), aun cuando la resolución judicial no fuere susceptible de ejecución.',
  h: 'Personas incluidas en el Registro de Alimentantes Morosos (RAM).',
  i: 'Personas condenadas por delitos dolosos de orden federal u ordinario con inhabilitación desde segunda instancia confirmatoria (RAP).',
};

const ESTADO_TIPO_BADGE: Record<string, { label: string; cls: string }> = {
  APROBADO: { label: 'Verificado', cls: 'bg-green-100 text-green-700' },
  RECHAZADO: { label: 'Rechazado', cls: 'bg-red-100 text-red-700' },
  PENDIENTE: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700' },
  SIN_CARGAR: { label: 'Sin cargar', cls: 'bg-gray-100 text-gray-600' },
};



const GRID_TABLERO = 'grid grid-cols-[minmax(0,12rem)_4rem_6rem_8rem_minmax(0,1fr)_11rem] gap-3';


function NotaDocumentoCelda({ docId, valor, onGuardado }: { docId: string; valor: string; onGuardado: () => void }) {
  const [texto, setTexto] = useState(valor);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setTexto(valor); }, [valor]);
  const sucio = texto.trim() !== (valor || '').trim();

  const guardar = async () => {
    setGuardando(true);
    try {
      await documentoService.updateNota(docId, texto.trim() || null);
      toast.success('Nota guardada');
      onGuardado();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo guardar la nota');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="relative min-w-0">
      <textarea
        value={texto}
        onChange={e => setTexto(e.target.value)}
        placeholder="Nota..."
        rows={2}
        className="block w-full resize-y border border-amber-300 rounded-md px-2 pt-1.5 pb-7 text-xs bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-500"
      />
      <button
        onClick={guardar}
        disabled={!sucio || guardando}
        className="absolute bottom-1 right-1 border border-gray-400 text-gray-700 bg-white text-[11px] leading-none px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {guardando ? 'Guardando…' : 'Anotar'}
      </button>
    </div>
  );
}

const FilaInciso = forwardRef<{ guardarSiSucio: () => Promise<void> }, { fila: any; expedienteId: string; documentos: any[]; onGuardado: () => void; puedeGestionar: boolean }>(
  function FilaInciso({ fila, expedienteId, documentos, onGuardado, puedeGestionar }, ref) {
    const [estado, setEstado] = useState(fila.estado);
    const [comentario, setComentario] = useState(fila.comentario || '');
    const [documentoId, setDocumentoId] = useState(fila.documentoId || '');
    const [guardando, setGuardando] = useState(false);
    useEffect(() => {
      setEstado(fila.estado);
      setComentario(fila.comentario || '');
      setDocumentoId(fila.documentoId || '');
    }, [fila.estado, fila.comentario, fila.documentoId]);
    const sucio = estado !== fila.estado || comentario.trim() !== (fila.comentario || '').trim() || documentoId !== (fila.documentoId || '');

    const guardar = async () => {
      setGuardando(true);
      try {
        await candidaturaExpedienteService.actualizarInciso(expedienteId, fila.inciso, {
          estado,
          comentario: comentario.trim() || undefined,
          documentoId: documentoId || undefined,
        });
        onGuardado();
      } catch (err: any) {
        toast.error(`Inciso ${fila.inciso.toUpperCase()}: ${err?.response?.data?.error?.message || 'no se pudo guardar'}`);
        throw err;
      } finally {
        setGuardando(false);
      }
    };

    useImperativeHandle(ref, () => ({
      guardarSiSucio: async () => {
        if (sucio) await guardar();
      },
    }));

    return (
      <tr className="border-t border-gray-100">
        <td className="px-3 py-2 font-semibold uppercase align-top">{fila.inciso}</td>
        <td className="px-3 py-2 text-gray-600 align-top">{TEXTOS_INCISO[fila.inciso] || '—'}</td>
        <td className="px-3 py-2 align-top">
        <select
            value={estado}
            onChange={e => setEstado(e.target.value)}
            disabled={!puedeGestionar}
            className={`border rounded px-2 py-1 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed ${
              estado === 'VERIFICADO' ? 'bg-green-50 border-green-300 text-green-700' :
              estado === 'OBSERVADO' ? 'bg-red-50 border-red-300 text-red-700' :
              estado === 'SIN_FUENTE_CONSULTABLE' ? 'bg-sky-50 border-sky-300 text-sky-700' :
              estado === 'NO_APLICA' ? 'bg-gray-50 border-gray-300 text-gray-500' :
              'bg-amber-50 border-amber-300 text-amber-700'
            }`}
          >
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="VERIFICADO">VERIFICADO</option>
            <option value="OBSERVADO">OBSERVADO</option>
            <option value="SIN_FUENTE_CONSULTABLE">SIN_FUENTE_CONSULTABLE</option>
            <option value="NO_APLICA">NO_APLICA</option>
          </select>
        </td>
        <td className="px-3 py-2 align-top min-w-[16rem]">
        <textarea
            value={comentario}
            onChange={e => setComentario(e.target.value)}
            placeholder="Comentario..."
            rows={2}
            disabled={!puedeGestionar}
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs resize-y disabled:opacity-60 disabled:cursor-not-allowed"
          />
        </td>
        <td className="px-3 py-2 align-top">
        <select
            value={documentoId}
            onChange={e => setDocumentoId(e.target.value)}
            disabled={!puedeGestionar}
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">Ninguno</option>
            {documentos.map((doc: any) => (
              <option key={doc.id} value={doc.id}>{tipoDocLabels[doc.tipo] || doc.tipo}</option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2 align-top text-xs">
          {guardando ? <span className="text-gray-400">Guardando…</span> : sucio ? <span className="text-amber-600 font-medium">Sin guardar</span> : <span className="text-gray-300">—</span>}
        </td>
      </tr>
    );
  }
);

const ESTADO_BADGE: Record<string, string> = {
  PRESENTADO: 'bg-blue-100 text-blue-800',
  EN_ANALISIS: 'bg-amber-100 text-amber-800',
  OBSERVADO: 'bg-red-100 text-red-800',
  EN_SECRETARIO: 'bg-purple-100 text-purple-800',
  EN_TEP: 'bg-indigo-100 text-indigo-800',
  VIGENTE: 'bg-green-100 text-green-800',
  RECHAZADO: 'bg-red-100 text-red-800',
};

function construirBorradorElevarSecretario(expediente: any): string {
  const candidato = expediente?.candidato;
  return [
    '**Al Secretario Electoral.**',
    '',
    `**Partido:** ${expediente?.partido?.denominacion || ''}`,
    '',
    `Se eleva el expediente N° ${expediente?.numero} correspondiente a la candidatura de **${candidato?.apellido}, ${candidato?.nombre}** (DNI ${candidato?.dni}) al cargo de ${candidato?.cargo}. El análisis de la Secretaría Electoral Permanente concluyó.`,
    '',
    'Queda a su elevación al Tribunal Electoral Provincial.',
  ].join('\n');
}

function construirBorradorInformeTecnico(expediente: any, inhabilidades: any[]): string {
  const candidato = expediente?.candidato;
  const documentos = expediente?.documentos || [];
  const lineasIncisos = (inhabilidades || [])
    .map((i: any) => `- Inciso ${i.inciso.toUpperCase()}: ${i.estado}${i.comentario ? ` — ${i.comentario}` : ''}`)
    .join('\n');
  const lineasDocumentos = documentos.length > 0
  ? documentos.map((d: any) => `- ${tipoDocLabels[d.tipo] || d.tipo}: ${d.estadoRevisionAnalisis || 'sin revisar'}`).join('\n')
    : '- Sin documentación cargada.';

  return [
    '**Al Tribunal Electoral Provincial.**',
    '',
    `**Partido:** ${expediente?.partido?.denominacion || ''}`,
    '',
    `Se eleva al Tribunal Electoral Provincial (TEP) el informe técnico correspondiente a la candidatura de **${candidato?.apellido}, ${candidato?.nombre}** (DNI ${candidato?.dni}) al cargo de ${candidato?.cargo}, expediente N° ${expediente?.numero}.`,
    '',
    '**Control de inhabilidades (Art. 72 — Ley XII-21)**',
    '',
    lineasIncisos || '- Sin datos.',
    '',
    '**Documentación revisada**',
    '',
    lineasDocumentos,
    '',
    '**Recomendación**',
    '',
    'En virtud de lo expuesto, se recomienda al Tribunal Electoral Provincial OFICIALIZAR la candidatura, conforme a derecho.',
  ].join('\n');
}

const OBLIGATORIOS_TIPOS = TIPOS_CANDIDATURA;

const PASOS_STEPPER = [
  { n: 1, label: 'Presentado', estados: ['PRESENTADO'] },
  { n: 2, label: 'En análisis', estados: ['EN_ANALISIS'] },
  { n: 3, label: 'Observado', estados: ['OBSERVADO'] },
  { n: 4, label: 'En Secretario', estados: ['EN_SECRETARIO'] },
  { n: 5, label: 'En el TEP', estados: ['EN_TEP'] },
  { n: 6, label: 'Resuelto', estados: ['VIGENTE', 'RECHAZADO'] },
];

function formatFecha(fecha?: string | null): string {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
}

function formatFechaHora(fecha?: string | null): string {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleString('es-AR', {
    timeZone: 'America/Argentina/Buenos_Aires',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}
const TIPO_OBS_LABEL: Record<string, string> = {
  DATOS: 'Datos',
  DOCUMENTACION: 'Documentación',
  INHABILIDAD: 'Inhabilidad',
};

const OPCIONES_RESOLUCION_TEP_CANDIDATURA: OpcionAccion[] = [
  {
    key: 'tep_aprueba_cand',
    label: 'El TEP aprueba',
    descripcion: 'Oficializa la candidatura. El expediente queda vigente y el candidato oficializado.',
    tono: 'ok',
  },
  {
    key: 'tep_rechaza_cand',
    label: 'El TEP rechaza',
    descripcion: 'El expediente y el candidato quedan rechazados. El partido no cambia de estado.',
    tono: 'bad',
  },
  {
    key: 'tep_aplaza_cand',
    label: 'El TEP aplaza',
    descripcion:
      'El expediente vuelve a análisis para que la SEP curse una observación con lo que el ' +
      'Tribunal determinó. El candidato no cambia de estado.',
    tono: 'warn',
  },
];

function RespuestaObservacionCandidatura({ observacionId, expedienteId, candidatoId, partidoId, nombrePartido, onSuccess }: { observacionId: string; expedienteId: string; candidatoId: string; partidoId?: string; nombrePartido?: string; onSuccess: () => void }) {
  const prefijoSEP = `Respuesta cargada por la SEP en nombre del partido (${nombrePartido || 'partido'}).`;
  const [abierto, setAbierto] = useState(false);
  const [respuesta, setRespuesta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [adjuntos, setAdjuntos] = useState<Array<{ id: string; tipo: string; titulo: string }>>([]);
  const [tipoASubir, setTipoASubir] = useState('');
  const [subiendo, setSubiendo] = useState(false);

  const subirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo || !tipoASubir) return;
    setSubiendo(true);
    try {
      // Los tipos del candidato van por candidatoService (vincula por candidatoId).
      // El resto son documentos del PARTIDO (símbolo, notas, etc.) y van por el
      // endpoint genérico, vinculados al expediente/partido.
      let doc: any;
      if (TIPOS_CANDIDATURA.includes(tipoASubir)) {
        const res = await candidatoService.subirDocumento(candidatoId, tipoASubir, archivo);
        doc = res.data.data;
      } else {
        const formData = new FormData();
        formData.append('archivo', archivo);
        formData.append('tipo', tipoASubir);
        formData.append('titulo', tipoDocLabels[tipoASubir] || tipoASubir);
        formData.append('expedienteId', expedienteId);
        if (partidoId) formData.append('partidoId', partidoId);
        const res = await documentoService.upload(formData);
        doc = res.data.data;
      }
      setAdjuntos(prev => [...prev, { id: doc.id, tipo: tipoASubir, titulo: doc.titulo || doc.nombreOriginal }]);
      setTipoASubir('');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo subir el archivo');
    } finally {
      setSubiendo(false);
    }
  };

  const removerAdjunto = (id: string) => setAdjuntos(prev => prev.filter(a => a.id !== id));

  const enviar = async () => {
    const detalle = respuesta.trim();
    const textoFinal = `${prefijoSEP}${detalle ? ' ' + detalle : ''}`;
    setEnviando(true);
    try {
      await api.post(`/expedientes/${expedienteId}/observaciones/${observacionId}/responder`, {
        texto: textoFinal,
        documentoIds: adjuntos.map(a => a.id),
      });
      toast.success('Respuesta registrada');
      setRespuesta('');
      setAdjuntos([]);
      setAbierto(false);
      onSuccess();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo enviar la respuesta');
    } finally {
      setEnviando(false);
    }
  };

  if (!abierto) {
    return (
      <div className="pl-8">
        <button
          type="button"
          onClick={() => setAbierto(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-7 4h7M3 6h18" />
          </svg>
          Responder en nombre del partido
        </button>
      </div>
    );
  }

  return (
    <div className="pl-8">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">Registrar respuesta del partido (recibida fuera del sistema):</p>
          <button type="button" onClick={() => setAbierto(false)} className="text-xs text-gray-500 hover:text-gray-700 underline">
            Cancelar
          </button>
        </div>
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-2 text-sm text-gray-600 italic">
          {prefijoSEP}
        </div>
        <RichTextEditor
          value={respuesta}
          onChange={setRespuesta}
          minHeight={100}
          placeholder="Detalle (opcional): aclaraciones sobre la respuesta recibida..."
        />
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Adjuntar documentos (opcional)</label>
          <div className="space-y-2">
            {adjuntos.map(adj => (
              <div key={adj.id} className="flex items-center gap-2 p-2 bg-white rounded border text-sm">
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium flex-shrink-0">
                  {tipoDocLabels[adj.tipo] || adj.tipo}
                </span>
                <span className="flex-1 truncate">{adj.titulo}</span>
                <button type="button" onClick={() => removerAdjunto(adj.id)} className="text-red-500 hover:text-red-700 flex-shrink-0" title="Quitar de la respuesta">
                  ✕
                </button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={tipoASubir}
                onChange={e => setTipoASubir(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-2 text-sm"
              >
                <option value="">Elegí el tipo de documento...</option>
                {TIPOS_RESPUESTA_AGRUPACION.map((tipo: string) => (
                  <option key={tipo} value={tipo}>{tipoDocLabels[tipo] || tipo}</option>
                ))}
              </select>
              <label className={`inline-flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-sm text-gray-600 ${tipoASubir && !subiendo ? 'hover:bg-white cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                {subiendo ? 'Subiendo...' : 'Subir documento'}
                <input type="file" className="hidden" disabled={!tipoASubir || subiendo} onChange={subirArchivo} />
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <button onClick={enviar} disabled={enviando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
            {enviando ? 'Enviando...' : 'Registrar respuesta'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ESTADO_LABEL: Record<string, string> = {
  PRESENTADO: 'Presentado',
  EN_ANALISIS: 'En análisis',
  OBSERVADO: 'Observado',
  EN_SECRETARIO: 'En Secretario',
  EN_TEP: 'En el TEP',
  VIGENTE: 'Vigente',
  RECHAZADO: 'Rechazado',
};

// El comentario de un movimiento puede traer anexada la lista de destinatarios
// notificados por mail, con el formato: "<texto>\n\n📧 Notificaciones: ✅ a@b, ❌ c@d".
// Mismo parseo que usa el historial de Reempadronamiento/Alianzas.
function parseMovimientoComentario(raw?: string) {
  if (!raw) return { texto: '', notificaciones: [] as Array<{ ok: boolean; email: string }> };
  const marker = '📧 Notificaciones:';
  const idx = raw.indexOf(marker);
  if (idx === -1) return { texto: raw.trim(), notificaciones: [] };
  const texto = raw.slice(0, idx).trim();
  const notificaciones = raw
    .slice(idx + marker.length)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => ({
      ok: s.startsWith('✅'),
      email: s.replace(/^✅|^❌/, '').trim(),
    }));
  return { texto, notificaciones };
}

type Tab = 'documentacion' | 'observaciones' | 'inhabilidades' | 'candidato' | 'historial';

export default function ExpedienteCandidaturaDetailPage() {
  const { user } = useAuth();
  // Mesa de Entradas ve la gestión de candidaturas pero es de solo lectura: no puede
  // ejecutar ninguna acción. Mismo criterio que ExpedienteDetailPage.tsx (puedeGestionar).
  const puedeGestionar = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>('documentacion');
  const [obsTipo, setObsTipo] = useState<'DATOS' | 'DOCUMENTACION' | 'INHABILIDAD'>('DOCUMENTACION');
  const [obsDescripcion, setObsDescripcion] = useState('');
  const [obsDocumentoId, setObsDocumentoId] = useState('');
  const [obsPlazoDias, setObsPlazoDias] = useState(2);
  // 0 en el desplegable = "Sin plazo".
  const obsSinPlazo = obsPlazoDias === 0;
  const [obsAdjuntos, setObsAdjuntos] = useState<File[]>([]);  
  const [enviandoObs, setEnviandoObs] = useState(false);
  const [guardandoBorrador, setGuardandoBorrador] = useState(false);
  const [notaPartido, setNotaPartido] = useState<string | null>(null);
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [tipoSeleccionado, setTipoSeleccionado] = useState<string | null>(null);
  const [informeConfig, setInformeConfig] = useState<InformeSimpleConfig | null>(null);
  const [modalCruceExcel, setModalCruceExcel] = useState<'RAP' | 'RAM' | null>(null);
  const [archivoCruce, setArchivoCruce] = useState<File | null>(null);
  const [resultadoCruce, setResultadoCruce] = useState<{ evaluado: boolean; encontrado: boolean; candidato: string | null; inciso: string } | null>(null);
  const [enviandoCruce, setEnviandoCruce] = useState(false);
  const [configTep, setConfigTep] = useState<ResolucionTepConfig | null>(null);
  const [opcionesTep, setOpcionesTep] = useState(false);
  const filaIncisoRefs = useRef<Record<string, { guardarSiSucio: () => Promise<void> } | null>>({});
  const [guardandoTodo, setGuardandoTodo] = useState(false);

  const { data: expediente, isLoading } = useQuery({
    queryKey: ['candidatura-expediente', id],
    queryFn: async () => (await candidaturaExpedienteService.obtener(id!)).data.data,
    enabled: !!id,
  });

  const { data: timeline } = useQuery({
    queryKey: ['candidatura-timeline', id],
    queryFn: async () => (await candidaturaExpedienteService.timeline(id!)).data.data,
    enabled: !!id,
  });

  const { data: inhabilidades } = useQuery({
    queryKey: ['candidatura-inhabilidades', id],
    queryFn: async () => (await candidaturaExpedienteService.inhabilidades(id!)).data.data,
    enabled: !!id,
  });

  const { data: secciones } = useQuery({
    queryKey: ['secciones-tipo-docs', id],
    queryFn: () => seccionesDocumentoService.listar(id!),
    enabled: !!id,
  });

  const { data: borrador } = useQuery({
    queryKey: ['candidatura-borrador-observacion', id],
    queryFn: async () => (await candidaturaExpedienteService.obtenerBorrador(id!)).data.data,
    enabled: !!id && puedeGestionar,
  });

  useEffect(() => {
    if (borrador?.texto && !obsDescripcion) {
      setObsDescripcion(borrador.texto);
    }
  }, [borrador]);

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['candidatura-expediente', id] });
    queryClient.invalidateQueries({ queryKey: ['candidatura-timeline', id] });
  };

  const iniciarAnalisis = async () => {
    try {
      await candidaturaExpedienteService.iniciarAnalisis(id!);
      toast.success('Análisis iniciado');
      invalidar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo iniciar el análisis');
    }
  };
  
  const elegirResolucionTep = (key: string) => {
    if (key === 'tep_aprueba_cand') {
      setConfigTep({
        endpoint: 'tep-aprueba',
        basePath: 'candidaturas/expedientes',
        titulo: 'TEP aprueba la candidatura',
        requiereMotivo: false,
        tono: 'ok',
        successMsg: 'Candidatura oficializada',
        confirmMsg: '¿Confirmás que el TEP aprobó esta candidatura?',
        confirmLabel: 'Registrar aprobación',
        conAvisoPartido: true,
        labelAvisoPartido: 'Notificar al partido por correo',
      });
    } else if (key === 'tep_rechaza_cand') {
      setConfigTep({
        endpoint: 'tep-rechaza',
        basePath: 'candidaturas/expedientes',
        titulo: 'TEP rechaza la candidatura',
        requiereMotivo: true,
        tono: 'bad',
        successMsg: 'Rechazo registrado',
        confirmMsg: '¿Confirmás que el TEP rechazó esta candidatura?',
        confirmLabel: 'Registrar rechazo',
        conAvisoPartido: true,
        labelAvisoPartido: 'Notificar al partido por correo',
      });
    } else if (key === 'tep_aplaza_cand') {
      setConfigTep({
        endpoint: 'tep-aplaza',
        basePath: 'candidaturas/expedientes',
        titulo: 'TEP aplaza la resolución',
        requiereMotivo: true,
        tono: 'bad',
        successMsg: 'Resolución registrada — el expediente vuelve a análisis',
        confirmMsg: '¿Confirmás que el TEP aplazó la resolución sobre esta candidatura?',
        confirmLabel: 'Registrar aplazo',
        conAvisoPartido: true,
        labelAvisoPartido: 'Notificar al partido por correo',
      });
    }
  };

  const guardarTodosLosIncisos = async () => {
    setGuardandoTodo(true);
    try {
      const refs = Object.values(filaIncisoRefs.current).filter(Boolean) as Array<{ guardarSiSucio: () => Promise<void> }>;
      const resultados = await Promise.allSettled(refs.map(r => r.guardarSiSucio()));
      const fallidos = resultados.filter(r => r.status === 'rejected').length;
      if (fallidos === 0) {
        toast.success('Incisos guardados');
      } else {
        toast.error(`${fallidos} inciso(s) no se pudieron guardar`);
      }
    } finally {
      setGuardandoTodo(false);
    }
  };

  const cerrarModalCruce = () => {
    setModalCruceExcel(null);
    setArchivoCruce(null);
    setResultadoCruce(null);
  };

  const ejecutarCruceExcel = async () => {
    if (!modalCruceExcel || !archivoCruce) {
      toast.error('Elegí el archivo Excel');
      return;
    }
    setEnviandoCruce(true);
    try {
      const { data } = await candidaturaExpedienteService.cruzarExcel(modalCruceExcel, archivoCruce, id);
      setResultadoCruce(data?.data?.expedienteActual ?? { evaluado: false, encontrado: false, candidato: null, inciso: '' });
      invalidar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo procesar el Excel');
    } finally {
      setEnviandoCruce(false);
    }
  };

  const revisarDocumento = async (documentoId: string, estado: 'APROBADO' | 'RECHAZADO') => {
    try {
      await documentoService.updateEstadoRevision(documentoId, estado, undefined, 'ANALISIS');
      toast.success(estado === 'APROBADO' ? 'Documento aprobado' : 'Documento rechazado');
      invalidar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo actualizar el documento');
    }
  };

  const abrirObservacionDesdeDocumento = (documentoId: string) => {
    setObsDocumentoId(documentoId);
    setObsTipo('DOCUMENTACION');
    setTab('observaciones');
  };

  const abrirObservacionAutomatica = (texto: string) => {
    setObsDocumentoId('');
    setObsTipo('DOCUMENTACION');
    setObsDescripcion(texto);
    setTab('observaciones');
  };
  const abrirAdjuntoObservacion = async (observacionId: string, adjuntoId: string) => {
    try {
      window.open(await expedienteService.urlAdjuntoObservacion(id!, observacionId, adjuntoId), '_blank');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo abrir el adjunto');
    }
  };

  const crearObservacion = async () => {
    if (!obsDescripcion.trim()) {
      toast.error('Escribí una descripción para la observación');
      return;
    }
    if (superaTotalAdjuntos(obsAdjuntos)) {
      toast.error(`Los adjuntos suman más de ${MAX_MB_TOTAL_OBSERVACION} MB. Mandá los más pesados en otra observación.`);
      return;
    }
    setEnviandoObs(true);
    try {
      const datos = new FormData();
      datos.append('tipo', obsTipo);
      datos.append('descripcion', obsDescripcion.trim());
      if (obsDocumentoId) datos.append('documentoId', obsDocumentoId);
      if (obsSinPlazo) datos.append('sinPlazo', 'true');
      else datos.append('plazoDias', String(obsPlazoDias));
      obsAdjuntos.forEach((archivo) => datos.append('archivos', archivo));
      await candidaturaExpedienteService.crearObservacion(id!, datos);
      toast.success('Observación generada');
      setObsDescripcion('');
      setObsDocumentoId('');
      setObsTipo('DOCUMENTACION');
      setObsPlazoDias(2);
      setObsAdjuntos([]);
      invalidar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo generar la observación');
    } finally {
      setEnviandoObs(false);
    }
  };

  const guardarBorradorObservacion = async () => {
    if (!obsDescripcion.trim()) {
      toast.error('Escribí algo para guardar como borrador');
      return;
    }
    setGuardandoBorrador(true);
    try {
      await candidaturaExpedienteService.guardarBorrador(id!, obsDescripcion.trim());
      toast.success('Borrador guardado');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo guardar el borrador');
    } finally {
      setGuardandoBorrador(false);
    }
  };

  const resolverObservacion = async (observacionId: string, estado: 'RESUELTA' | 'RECHAZADA') => {
    try {
      await candidaturaExpedienteService.resolverObservacion(observacionId, { estado });
      toast.success(estado === 'RESUELTA' ? 'Observación marcada como resuelta' : 'Observación rechazada');
      invalidar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo actualizar la observación');
    }
  };

  const reabrirObservacion = async (observacionId: string) => {
    try {
      await candidaturaExpedienteService.reabrirObservacion(observacionId);
      toast.success('Observación reabierta');
      invalidar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo reabrir la observación');
    }
  };

  const guardarNotaPartido = async () => {
    if (!expediente?.partido?.id || notaPartido === null) return;
    setGuardandoNota(true);
    try {
      await partidoService.updateNota(expediente.partido.id, notaPartido.trim() || null);
      toast.success('Nota actualizada');
      invalidar();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo guardar la nota');
    } finally {
      setGuardandoNota(false);
    }
  };

  if (isLoading || !expediente) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const candidato = expediente.candidato;
  const documentos = expediente.documentos || [];
  const observacionesActivas = (expediente.observaciones || []).filter((o: any) => o.estado === 'PENDIENTE' || o.estado === 'RESPONDIDA');
  // La más nueva arriba, igual que en el detalle general.
  const todasLasObservaciones = [...(expediente.observaciones || [])].sort((a: any, b: any) => new Date(b.creadoEn).getTime() - new Date(a.creadoEn).getTime());
  const puedeElevar = puedeGestionar && (expediente.estado === 'EN_ANALISIS' || expediente.estado === 'OBSERVADO');
  const incisosPendientes = (inhabilidades || [])
    .filter((i: any) => i.estado === 'PENDIENTE')
    .map((i: any) => i.inciso.toUpperCase());
  const avisoPendientes = incisosPendientes.length > 0
    ? `ATENCIÓN: hay incisos de la grilla de inhabilidades en PENDIENTE (${incisosPendientes.join(', ')}). Podés elevar igual, pero quedan sin resolver.\n\n`
    : '';
  const avisoObservaciones = observacionesActivas.length > 0
    ? `ATENCIÓN: ${observacionesActivas.length === 1 ? 'hay 1 observación abierta' : `hay ${observacionesActivas.length} observaciones abiertas`}. Podés elevar igual: el informe deja constancia.\n\n`
    : '';
  const avisos = `${avisoPendientes}${avisoObservaciones}`;
  const esRechazado = expediente.estado === 'RECHAZADO';
  const pasoActualIdx = PASOS_STEPPER.findIndex(p => p.estados.includes(expediente.estado));
  const vencido = Boolean(expediente.fechaVencimiento && new Date(expediente.fechaVencimiento) < new Date());

  // --- Observación automática: qué falta o fue rechazado de los 8 tipos obligatorios ---
  const getDocDeTipo = (tipo: string) => {
    const matches = documentos.filter((d: any) => d.tipo === tipo);
    if (!matches.length) return undefined;
    const prioridad: Record<string, number> = { RECHAZADO: 0, PENDIENTE: 1, APROBADO: 2 };
    return matches.sort((a: any, b: any) => (prioridad[a.estadoRevisionAnalisis] ?? 3) - (prioridad[b.estadoRevisionAnalisis] ?? 3))[0];
  };
  const problemasDoc = OBLIGATORIOS_TIPOS
    .map(tipo => ({ tipo, doc: getDocDeTipo(tipo) }))
    .filter(({ doc }) => !doc || doc.estadoRevisionAnalisis === 'RECHAZADO');

  const textoObservacionAutomatica = problemasDoc.length > 0
    ? 'Se observa la documentación presentada por los siguientes motivos:\n' +
      problemasDoc.map(({ tipo, doc }) => {
        const label = tipoDocLabels[tipo];
        if (!doc) return `- ${label}: no presentado.`;
        return `- ${label}: rechazado.`;
      }).join('\n')
    : '';

  const TABS: Array<{ key: Tab; label: string; count?: number }> = [
    { key: 'documentacion', label: 'Documentación', count: documentos.length },
    { key: 'observaciones', label: 'Observaciones', count: observacionesActivas.length },
    { key: 'inhabilidades', label: 'Inhabilidades Art. 72' },
    { key: 'candidato', label: 'Datos del candidato' },
    { key: 'historial', label: 'Historial' },
  
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="bg-white rounded-lg shadow p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => navigate('/candidaturas/gaiman/bandeja')} className="text-gray-400 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-gray-900">{expediente.numero} — Candidatura</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${ESTADO_BADGE[expediente.estado] || 'bg-gray-100 text-gray-700'}`}>
                {expediente.estado}
              </span>
              {vencido && (
                <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium">VENCIDO</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              {expediente.partido && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                  {expediente.partido.sigla ? `${expediente.partido.sigla} - ` : ''}{expediente.partido.denominacion}
                </span>
              )}
              {candidato && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  {candidato.apellido}, {candidato.nombre}
                </span>
              )}
              {expediente.fechaPresentacion && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {formatFecha(expediente.fechaPresentacion)}
                </span>
              )}
              {expediente.asignado ? (
                <span>Asignado a <strong>{expediente.asignado.nombre} {expediente.asignado.apellido}</strong></span>
              ) : (
                
                <span className="italic">Sin asignar</span>
              )}
              {vencido && expediente.fechaVencimiento && (
                <span className="text-orange-600 font-semibold">⏱ vencido el {formatFecha(expediente.fechaVencimiento)}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
          {puedeGestionar && expediente.estado === 'PRESENTADO' && (
              <button onClick={iniciarAnalisis} className="px-4 py-2 text-white text-sm font-medium rounded-lg transition whitespace-nowrap bg-blue-600 hover:bg-blue-700">
                Iniciar análisis
              </button>
            )}

            {puedeElevar && (
              <button
                onClick={async () => {
                  let borrador = '';
                  try {
                    borrador = await candidaturaExpedienteService.borradorElevarSecretario(expediente.id);
                  } catch (err: any) {
                    toast.error(err?.response?.data?.error?.message || 'No se pudo armar el borrador del informe');
                    return;
                  }
                  setInformeConfig({
                    endpoint: 'elevar-secretario',
                    basePath: 'candidaturas/expedientes',
                    titulo: 'Informe al Secretario — Candidatura',
                    textoInicial: conEncabezadoInforme(borrador, {
                      destinatario: 'SECRETARIO',
                      nombrePartido: expediente.partido?.denominacion,
                    }),
                    ayuda: `${avisos}Redactá el informe al Secretario Electoral sobre el análisis de la candidatura. El cuerpo viene breve a propósito: el detalle ya está en la grilla de inhabilidades y en Documentación.`,
                    confirmMsg: `${avisos}¿Elevar el informe al Secretario? El expediente pasa a "En Secretario" y queda a su resolución.`,  
                  successMsg: 'Informe elevado al Secretario',
                  conHistorial: true,
                  labelGuardar: 'Elevar al Secretario',
                });
              }}
              className="px-4 py-2 text-white text-sm font-medium rounded-lg transition whitespace-nowrap bg-purple-600 hover:opacity-90"
              >
                Elevar al Secretario →
              </button>
            )}
            {expediente.estado === 'EN_SECRETARIO' && (
              <button
              onClick={() => setInformeConfig({
                endpoint: 'informe-tecnico',
                basePath: 'candidaturas/expedientes',
                titulo: 'Informe técnico al TEP — Candidatura',
                textoInicial: construirBorradorInformeTecnico(expediente, inhabilidades),
                ayuda: `${avisos}Redactá el informe técnico que se eleva al Tribunal Electoral Provincial. El texto ya incluye el resultado de la grilla de inhabilidades y la documentación revisada. Tildá al costado las observaciones que quieras incluir, y podés editar todo antes de confirmar.`,
                confirmMsg: `${avisos}¿Elevar el expediente al TEP con este informe técnico?`,
                successMsg: 'Informe técnico elevado al TEP',
                conHistorial: true,
                conAvisoPartido: true,
                labelAvisoPartido: 'Avisar al partido por correo que el expediente avanzó',
                subtextoAvisoPartido: 'El informe NO se adjunta: es documentación interna del organismo. Solo se le avisa del avance.',
              })}
              className="px-4 py-2 text-white text-sm font-medium rounded-lg transition whitespace-nowrap bg-indigo-600 hover:bg-indigo-700"
            >
              Elevar al TEP
            </button>
            )}
            {expediente.estado === 'EN_TEP' && (
              <button
                onClick={() => setOpcionesTep(true)}
                className="px-4 py-2 text-white text-sm font-medium rounded-lg transition whitespace-nowrap bg-cyan-700 hover:bg-cyan-800"
              >
                Resolución TEP
              </button>
            )}
            {(expediente.estado === 'EN_SECRETARIO' || expediente.estado === 'EN_TEP') && (
              <button
                onClick={() => {
                  const deshaceElevacion = expediente.estado === 'EN_TEP';
                  setInformeConfig({
                    endpoint: 'devolver-sep',
                    basePath: 'candidaturas/expedientes',
                    titulo: deshaceElevacion ? 'Deshacer la elevación al TEP' : 'Devolver el expediente a la SEP',
                    textoInicial: deshaceElevacion
                      ? 'Se deja sin efecto la elevación al Tribunal Electoral Provincial del presente expediente de candidatura, por los motivos que se exponen a continuación.\n\n[Detallar acá los motivos por los que se deshace la elevación.]'
                      : 'Se devuelve el presente expediente de candidatura a la Secretaría Electoral Permanente, por los motivos que se exponen a continuación.\n\n[Detallar acá las observaciones que motivan la devolución.]',
                    ayuda: 'Explicá por qué devolvés el expediente. Queda guardado como informe interno; el partido NO recibe copia.',
                    confirmMsg: deshaceElevacion
                      ? '¿Deshacer la elevación al TEP? El expediente vuelve al Secretario, sin pasar por el Tribunal.'
                      : '¿Devolver el expediente a la SEP? Vuelve a análisis.',
                    successMsg: deshaceElevacion ? 'Elevación al TEP deshecha' : 'Expediente devuelto a la SEP',
                    conHistorial: true,
                    labelGuardar: 'Confirmar devolución',
                  });
                }}
                className="px-4 py-2 text-white text-sm font-medium rounded-lg transition whitespace-nowrap bg-amber-600 hover:bg-amber-700"
              >
                Volver atrás
              </button>
            )}
          </div>
        </div>

        {!esRechazado && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center">
              {PASOS_STEPPER.map((p, i) => (
                <div key={p.n} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border-2 ${
                      i < pasoActualIdx ? 'bg-green-500 border-green-500 text-white' :
                      i === pasoActualIdx ? 'bg-orange-100 border-orange-500 text-orange-700' :
                      'bg-gray-50 border-gray-300 text-gray-400'
                    }`}>
                      {i < pasoActualIdx ? '✓' : i + 1}
                    </div>
                    <span className={`text-xs mt-1 whitespace-nowrap ${
                      i === pasoActualIdx ? 'font-medium text-orange-700' :
                      i < pasoActualIdx ? 'text-green-600' : 'text-gray-400'
                    }`}>{p.label}</span>
                  </div>
                  {i < PASOS_STEPPER.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 mb-4 ${i < pasoActualIdx ? 'bg-green-400' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {expediente.estado === 'OBSERVADO' && expediente.fechaVencimiento && (
        <div className={`border rounded-lg p-3 text-sm ${vencido ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          Plazo de subsanación {vencido ? 'venció el' : 'vence el'} <strong>{formatFecha(expediente.fechaVencimiento)}</strong>.
        </div>
      )}

      <div className="bg-white rounded-xl shadow">
        <div className="border-b border-gray-100 flex gap-1 px-2 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.count !== undefined && (
                <span className={`ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${tab === t.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === 'documentacion' && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-white flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Observación automática</h2>
                  {problemasDoc.length === 0 ? (
                    <p className="text-xs text-green-600 font-medium mt-1">Documentación completa — no hay problemas.</p>
                  ) : (
                    <p className="text-xs text-amber-600 mt-1">{problemasDoc.length} documento{problemasDoc.length === 1 ? '' : 's'} con problemas</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => abrirObservacionAutomatica(textoObservacionAutomatica)}
                    disabled={problemasDoc.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Generar observación {problemasDoc.length > 0 && `(${problemasDoc.length})`}
                  </button>
                  <Link
                    to={`/documentos?expedienteId=${expediente.id}${expediente.partido ? `&partidoId=${expediente.partido.id}` : ''}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
                  >
                    Ver todos los documentos →
                  </Link>
                </div>
              </div>

              {expediente.partido && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                  <h2 className="text-sm font-semibold text-amber-800 uppercase tracking-wide">Nota general del partido</h2>
                  <p className="text-xs text-amber-700 mt-1 mb-2">Es del partido, no de este expediente — la misma nota se ve en el listado de partidos.</p>
                  <textarea
                    rows={3}
                    defaultValue={expediente.partido.nota || ''}
                    onChange={e => setNotaPartido(e.target.value)}
                    placeholder="Nota general del partido..."
                    className="w-full border border-amber-200 rounded-lg p-3 text-sm bg-white resize-y"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={guardarNotaPartido}
                      disabled={notaPartido === null || guardandoNota}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                      >
                      {guardandoNota ? 'Guardando...' : 'Anotar'}
                    </button>
                  </div>
                </div>
              )}

              <PanelSeccionesDocumentacion
                expedienteId={expediente.id}
                partidoId={expediente.partido?.id}
                secciones={secciones || []}
                dimension="ANALISIS"
                tituloSeccion="Estado de la documentación — análisis"
                puedeEditarNota={puedeGestionar}
                mostrarNota={true}
              />
            </div>
          )}

          {tab === 'observaciones' && (
            <div className="space-y-4">
              {puedeGestionar && !['BORRADOR', 'PRESENTADO'].includes(expediente.estado) && (
                <div className="border border-yellow-200 rounded-lg">
                  <div className="px-4 py-3 border-b bg-yellow-50 rounded-t-lg">
                    <h2 className="text-sm font-medium text-yellow-800">Panel de Revisión</h2>
                    {expediente.asignado && (
                      <p className="text-xs text-yellow-600 mt-1">Revisando: {expediente.asignado.nombre} {expediente.asignado.apellido}</p>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    {!['EN_ANALISIS', 'OBSERVADO'].includes(expediente.estado) && (
                      <p role="note" className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                        La observación se registra y se notifica al partido, pero el expediente no cambia de estado.
                      </p>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Observaciones</label>
                      <RichTextEditor
                        value={obsDescripcion}
                        onChange={setObsDescripcion}
                        minHeight={160}
                        placeholder="Escriba las observaciones sobre la documentación o los datos del candidato..."
                      />
                    </div>
                    <AdjuntosInforme
                      archivos={obsAdjuntos}
                      onChange={setObsAdjuntos}
                      titulo="Adjuntar documentos (opcional)"
                      ayuda="Por ejemplo, una resolución para notificar. Hasta 50 MB por archivo y 50 MB en total. El partido los descarga desde el expediente; los PDF livianos también le llegan por mail."
                      maxMb={MAX_MB_TOTAL_OBSERVACION}
                      avisoCorreo={false}
                    />
                    <div className="flex flex-wrap gap-2 items-center">
                      <button onClick={guardarBorradorObservacion} disabled={!obsDescripcion.trim() || guardandoBorrador}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg border hover:bg-gray-200 text-sm disabled:opacity-50">
                        {guardandoBorrador ? 'Guardando...' : 'Guardar Borrador'}
                      </button>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <label className="text-xs text-gray-500">Plazo</label>
                          <select value={obsPlazoDias} onChange={e => setObsPlazoDias(parseInt(e.target.value, 10))}
                            className="border border-blue-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                            <option value={1}>1 día</option>
                            <option value={2}>2 días</option>
                            <option value={0}>Sin plazo</option>
                          </select>
                        </div>
                        {!obsSinPlazo && (
                          <p className="text-xs text-gray-500">
                            Vence: {formatFechaHora(new Date(Date.now() + obsPlazoDias * 24 * 60 * 60 * 1000).toISOString())}
                          </p>
                        )}
                      </div>
                      <button onClick={crearObservacion} disabled={!obsDescripcion.trim() || enviandoObs}
                        className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm disabled:opacity-50">
                        {enviandoObs ? 'Enviando...' : 'Generar observación'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {todasLasObservaciones.length === 0 && <p className="text-sm text-gray-400">No hay observaciones activas.</p>}
              <div className="space-y-4">
                {todasLasObservaciones.map((obs: any) => {
                  // La nota de cada observación es la PRIMERA creada después de ella.
                  // Los documentos llegan del más nuevo al más viejo: sin ordenar, `find`
                  // devolvía siempre la última nota del expediente.
                  const pdfObs = (documentos || [])
                    .filter((d: any) =>
                      d.tipo === 'NOTA'
                      && d.titulo?.includes(expediente.numero)
                      && new Date(d.creadoEn).getTime() >= new Date(obs.creadoEn).getTime() - 5000)
                    .sort((a: any, b: any) => new Date(a.creadoEn).getTime() - new Date(b.creadoEn).getTime())[0];
                  return (
                    <div key={obs.id} className="space-y-2">
                      <div className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-orange-600 text-xs font-bold">S</span>
                        </div>
                        <div className="flex-1 bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-orange-800">Secretaría Electoral</span>
                              <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">
                                {TIPO_OBS_LABEL[obs.tipo] || obs.tipo}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-gray-500">{formatFechaHora(obs.creadoEn)}</span>
                              {puedeGestionar && (obs.estado === 'RESPONDIDA' || obs.estado === 'RESUELTA') && (
                                <input
                                  type="checkbox"
                                  checked={obs.estado === 'RESUELTA'}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      resolverObservacion(obs.id, 'RESUELTA');
                                    } else {
                                      reabrirObservacion(obs.id);
                                    }
                                  }}
                                  title={obs.estado === 'RESUELTA' ? 'Destildar: la observación vuelve a quedar abierta' : 'Dar por resuelta'}
                                  className="w-4 h-4 rounded border-gray-300 accent-green-600 cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{obs.texto}</p>
                          {obs.fechaLimite ? (
                            <p className="text-xs text-orange-600 font-medium mt-1">Plazo: {formatFechaHora(obs.fechaLimite)}</p>
                          ) : (
                            <p className="text-xs text-orange-600 font-medium mt-1">Sin plazo</p>
                          )}
                          {pdfObs && (
                            <button onClick={() => documentoService.download(pdfObs.id)}
                              className="mt-1 inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 underline">
                              Ver nota PDF
                            </button>
                          )}
                          {obs.adjuntos?.length > 0 && (
                            <ul className="mt-2 space-y-1" aria-label="Archivos adjuntos de la observación">
                              {obs.adjuntos.map((adj: any) => (
                                <li key={adj.id}>
                                  <button type="button" onClick={() => abrirAdjuntoObservacion(obs.id, adj.id)}
                                    className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 underline break-all text-left">
                                    📎 {adj.nombreOriginal}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      {obs.respuestaTexto && (
                        <div className="flex gap-3 pl-8">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 text-xs font-bold">A</span>
                          </div>
                          <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-xs font-medium text-blue-800">Apoderado</span>
                              <span className="text-xs text-gray-500">{formatFechaHora(obs.respuestaFecha)}</span>
                            </div>
                            <MarkdownDisplay content={obs.respuestaTexto} />
                            {(() => {
                              const adjuntosRespuesta = (documentos || []).filter((d: any) => d.observacionRespuestaId === obs.id);
                              if (adjuntosRespuesta.length === 0) return null;
                              const pendientesCount = adjuntosRespuesta.filter((d: any) => d.estadoRevisionAnalisis === 'PENDIENTE').length;
                              return (
                                <div className="mt-2 space-y-1 border-t border-blue-200 pt-2">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-xs text-blue-600 font-medium">Archivos adjuntos:</p>
                                    {pendientesCount > 0 && (
                                      <Link
                                        to={`/documentos?expedienteId=${expediente.id}&revisarObs=${obs.id}`}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded-md"
                                        title="Revisar los adjuntos en /documentos: se abren los acordeones y se destacan en amarillo"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                        Revisar en Documentos ({pendientesCount} pendiente{pendientesCount === 1 ? '' : 's'})
                                      </Link>
                                    )}
                                  </div>
                                  {adjuntosRespuesta.map((doc: any) => (
                                    <button
                                      key={doc.id}
                                      onClick={() => documentoService.download(doc.id)}
                                      className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900"
                                    >
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wide">
                                        {tipoDocLabels[doc.tipo] || doc.tipo}
                                      </span>
                                      <span className="underline">{doc.titulo || doc.nombreOriginal}</span>
                                    </button>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      {puedeGestionar && obs.estado === 'PENDIENTE' && (
                        <RespuestaObservacionCandidatura
                        observacionId={obs.id}
                        expedienteId={expediente.id}
                        candidatoId={expediente.candidato?.id}
                        partidoId={expediente.partido?.id}
                        nombrePartido={expediente.partido?.denominacion}
                        onSuccess={invalidar}
                      />
                      )}
                      {puedeGestionar && obs.estado === 'RESPONDIDA' && (
                        <div className="pl-8">
                          <button onClick={() => resolverObservacion(obs.id, 'RECHAZADA')} className="text-xs text-red-600 hover:underline font-medium">
                            Rechazar respuesta
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'inhabilidades' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
              El inciso a se calcula solo contra el padrón electoral. Los incisos b, c, d, e, f y g arrancan en SIN_FUENTE_CONSULTABLE: el requisito rige, pero el sistema no tiene de dónde verificarlo; se pueden cambiar a mano. Los incisos h e i quedan pendientes hasta el cruce con el Excel del RAM y del RAP.
              </p>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                  <th className="text-left px-3 py-2 w-16">Inciso</th>
                    <th className="text-left px-3 py-2">Supuesto</th>
                    <th className="text-left px-3 py-2">Estado</th>
                    <th className="text-left px-3 py-2">Comentario</th>
                    <th className="text-left px-3 py-2">Documento</th>
                    <th className="text-left px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(inhabilidades || []).map((fila: any) => (
                    <FilaInciso
                    key={fila.inciso}
                    ref={(el: any) => { filaIncisoRefs.current[fila.inciso] = el; }}
                    fila={fila}
                    expedienteId={id!}
                    documentos={documentos.filter((d: any) => d.tipo !== 'NOTA')}
                    onGuardado={() => queryClient.invalidateQueries({ queryKey: ['candidatura-inhabilidades', id] })}
                    puedeGestionar={puedeGestionar}
                  />
                  ))}
                  {(!inhabilidades || inhabilidades.length === 0) && (
                    <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400">Todavía no se inició el análisis de este expediente.</td></tr>
                  )}
                </tbody>
              </table>
              {puedeGestionar && (
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setModalCruceExcel('RAP')}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cruzar con Excel del RAP
                </button>
                <button
                  onClick={() => setModalCruceExcel('RAM')}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cruzar con Excel del RAM
                </button>
                <button
                  onClick={guardarTodosLosIncisos}
                  disabled={guardandoTodo || !inhabilidades || inhabilidades.length === 0}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 ml-auto"
                >
                  {guardandoTodo ? 'Guardando...' : 'Guardar todo'}
                  </button>
              </div>
              )}
            </div>
          )}

          {tab === 'candidato' && (

            candidato ? (
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Apellido y nombre</td><td className="py-2 text-right font-medium">{candidato.apellido}, {candidato.nombre}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">DNI</td><td className="py-2 text-right font-medium">{candidato.dni}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Fecha de nacimiento</td><td className="py-2 text-right font-medium">{formatFecha(candidato.fechaNacimiento)}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Género</td><td className="py-2 text-right font-medium">{candidato.genero || '—'}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Nacionalidad</td><td className="py-2 text-right font-medium">{candidato.nacionalidad || '—'}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Domicilio</td><td className="py-2 text-right font-medium">{candidato.domicilioCalle} {candidato.domicilioNumero}, {candidato.localidad}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Residencia declarada</td><td className="py-2 text-right font-medium">{candidato.anosResidenciaDeclarada ?? '—'} años {candidato.mesesResidenciaDeclarada ? `y ${candidato.mesesResidenciaDeclarada} meses` : ''}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Cargo</td><td className="py-2 text-right font-medium">{candidato.cargo}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Nombre en boleta</td><td className="py-2 text-right font-medium">{candidato.nombreEnBoleta || '—'}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Teléfono</td><td className="py-2 text-right font-medium">{candidato.telefono || '—'}</td></tr>
                  <tr className="border-t border-gray-100"><td className="py-2 text-gray-500">Email</td><td className="py-2 text-right font-medium">{candidato.email || '—'}</td></tr>
                </tbody>
              </table>
            ) : <p className="text-sm text-gray-400">Sin datos de candidato</p>
          )}

{tab === 'historial' && (
            <div>
              <h3 className="text-base font-medium text-gray-900 mb-4">Historial de movimientos</h3>
              {timeline?.length ? (
                <div className="flow-root">
                  <ul className="-mb-8">
                    {timeline.map((mov: any, idx: number) => (
                      <li key={mov.id}>
                        <div className="relative pb-8">
                          {idx !== timeline.length - 1 && (
                            <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200" />
                          )}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white bg-blue-500">
                                <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-500">
                                <span className="font-medium text-gray-900">{mov.usuario?.nombre || ''} {mov.usuario?.apellido || 'Sistema'}</span>
                                {' · '}{formatFechaHora(mov.fecha)}
                              </p>
                              <p className="text-sm font-medium mt-1">{mov.accion}</p>
                              {mov.estadoAnterior && mov.estadoNuevo && mov.estadoAnterior !== mov.estadoNuevo && (
                                <p className="text-sm text-gray-600">
                                  {ESTADO_LABEL[mov.estadoAnterior] || mov.estadoAnterior} → <span className="font-medium">{ESTADO_LABEL[mov.estadoNuevo] || mov.estadoNuevo}</span>
                                </p>
                              )}
                              {(() => {
                                const { texto, notificaciones } = parseMovimientoComentario(mov.comentario);
                                return (
                                  <>
                                    {texto && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{texto}</p>}
                                    {notificaciones.length > 0 && (
                                      <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                                        <p className="text-xs font-medium text-gray-500 mb-1">Notificado por correo a:</p>
                                        <ul className="space-y-0.5">
                                          {notificaciones.map((n, i) => (
                                            <li key={i} className="flex items-center gap-1.5 text-xs text-gray-700">
                                              <span className={n.ok ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                                {n.ok ? '✓' : '✗'}
                                              </span>
                                              <span className="break-all">{n.email}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-gray-500 text-center text-sm">Sin movimientos</p>
              )}
            </div>
          )}
        </div>
      </div>

<ModalInformeSimple
        config={informeConfig}
        expedienteId={id!}
        onClose={() => setInformeConfig(null)}
        onGuardado={invalidar}
      />

{modalCruceExcel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="font-semibold text-gray-900 mb-4">Cruzar con Excel del {modalCruceExcel}</h3>
            {resultadoCruce ? (
              <>
                {!resultadoCruce.evaluado ? (
                  <div className="text-sm rounded-lg p-3 bg-gray-50 border border-gray-200 text-gray-700">
                    Este expediente no está en análisis, así que su candidato no se controló.
                  </div>
                ) : resultadoCruce.encontrado ? (
                  <div className="text-sm rounded-lg p-3 bg-red-50 border border-red-200 text-red-800">
                    <p className="font-semibold">{resultadoCruce.candidato} FIGURA en el listado del {modalCruceExcel}.</p>
                    <p className="mt-1">El inciso {resultadoCruce.inciso} quedó observado.</p>
                  </div>
                ) : (
                  <div className="text-sm rounded-lg p-3 bg-green-50 border border-green-200 text-green-800">
                    <p className="font-semibold">{resultadoCruce.candidato} NO figura en el listado del {modalCruceExcel}.</p>
                    <p className="mt-1">El inciso {resultadoCruce.inciso} quedó verificado.</p>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-3">
                  El listado también se cruzó con los demás candidatos de Gaiman en análisis. En cada expediente quedó el comentario y el movimiento en el historial.
                </p>
                <div className="flex justify-end mt-5">
                  <button onClick={cerrarModalCruce} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">Cerrar</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">
                  Se cruza contra todos los candidatos de Gaiman en análisis: los que aparezcan en este Excel quedan observados en el inciso correspondiente y el resto, verificados. A cada uno le queda el comentario y el movimiento en el historial.
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={e => setArchivoCruce(e.target.files?.[0] || null)}
                  className="text-sm"
                />
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={cerrarModalCruce} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancelar</button>
                  <button onClick={ejecutarCruceExcel} disabled={enviandoCruce || !archivoCruce} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm">
                    {enviandoCruce ? 'Procesando...' : 'Cruzar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}


<ModalResolucionTep
        config={configTep}
        expedienteId={id!}
        onClose={() => setConfigTep(null)}
        onResuelto={invalidar}
      />
      <ModalOpcionesAccion
        abierto={opcionesTep}
        titulo="Registrar la resolución del TEP"
        ayuda="Elegí qué resolvió el Tribunal. En el paso siguiente se cargan el N° de acta, la fecha y los archivos de la resolución."
        opciones={OPCIONES_RESOLUCION_TEP_CANDIDATURA}
        onClose={() => setOpcionesTep(false)}
        onElegir={(key) => {
          setOpcionesTep(false);
          elegirResolucionTep(key);
        }}
      />
    </div>
  );
}